import React, { useCallback, useEffect, useState } from 'react';
import store from './journalStore';

/* 🎁 Goodies — the playful layer.
   The reasons jar surfaces one "reason I love you" per day (same chit for
   both of you on any given day — it's deterministic by date), and the love
   coupons are redeemable promises with a permanent redemption stamp. */

const COUPON_EMOJIS = ['🎟️', '🍳', '💆', '🎬', '🤗', '🛍️', '🍕', '🌹'];

const dayIndex = () => Math.floor((Date.now() + new Date().getTimezoneOffset() * -60000) / 86400000);

const prettyWhen = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
};

export default function Goodies({ passcode, me }) {
  const [reasons, setReasons] = useState(null);
  const [coupons, setCoupons] = useState(null);
  const [jarOpen, setJarOpen] = useState(false);
  const [toast, setToast] = useState('');

  const [reasonText, setReasonText] = useState('');
  const [addingReason, setAddingReason] = useState(false);
  const [couponTitle, setCouponTitle] = useState('');
  const [couponEmoji, setCouponEmoji] = useState('🎟️');
  const [addingCoupon, setAddingCoupon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [rs, cs] = await Promise.all([store.reasons(passcode), store.coupons(passcode)]);
      setReasons(rs);
      setCoupons(cs);
    } catch (e) {
      setReasons([]);
      setCoupons([]);
      setErr('The goodies are hiding — try again in a moment.');
    }
  }, [passcode]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 2200);
  };

  const todays = reasons && reasons.length ? reasons[dayIndex() % reasons.length] : null;
  const todaysNum = reasons && reasons.length ? (dayIndex() % reasons.length) + 1 : 0;

  const addReason = async (e) => {
    e.preventDefault();
    if (!reasonText.trim()) return;
    setBusy(true);
    setErr('');
    try {
      await store.addReason(passcode, { text: reasonText.trim(), author: me });
      setReasonText('');
      setAddingReason(false);
      showToast('Into the jar it goes 🫙');
      await load();
    } catch (e2) {
      setErr(e2.message || 'Could not add that reason.');
    } finally {
      setBusy(false);
    }
  };

  const delReason = async (id) => {
    try {
      await store.delReason(passcode, id);
      await load();
    } catch (e) {
      /* ignore */
    }
  };

  const addCoupon = async (e) => {
    e.preventDefault();
    if (!couponTitle.trim()) return;
    setBusy(true);
    setErr('');
    try {
      await store.addCoupon(passcode, { title: couponTitle.trim(), emoji: couponEmoji, from: me });
      setCouponTitle('');
      setCouponEmoji('🎟️');
      setAddingCoupon(false);
      showToast('Coupon printed 🎟️');
      await load();
    } catch (e2) {
      setErr(e2.message || 'Could not add that coupon.');
    } finally {
      setBusy(false);
    }
  };

  const redeem = async (c) => {
    setBusy(true);
    setErr('');
    try {
      await store.redeemCoupon(passcode, c.id, me);
      showToast(`Redeemed! ${c.from} owes you this now 😏`);
      await load();
    } catch (e2) {
      showToast(e2.message || 'Could not redeem.');
    } finally {
      setBusy(false);
    }
  };

  const delCoupon = async (id) => {
    try {
      await store.delCoupon(passcode, id);
      await load();
    } catch (e) {
      /* ignore */
    }
  };

  const loading = reasons === null || coupons === null;

  return (
    <div className="aj-goodies">
      <div className="aj-days-head">
        <h2 className="aj-days-title">the goodies</h2>
        <p className="aj-days-sub">a jar of reasons and a stack of promises. spend wisely. 😏</p>
      </div>

      {loading ? (
        <div className="aj-muted" style={{ textAlign: 'center' }}>unwrapping…</div>
      ) : (
        <>
          {/* ───────── reasons jar ───────── */}
          <div className="aj-jar">
            <div className="aj-ev-eyebrow">🫙 today&rsquo;s reason I love you</div>
            {todays ? (
              <>
                <div className="aj-jar-text">“{todays.text}”</div>
                <div className="aj-jar-meta">
                  reason #{todaysNum} of {reasons.length} · from {todays.author} · a new one every midnight
                </div>
              </>
            ) : (
              <div className="aj-muted">the jar is empty — drop the first reason in. 💕</div>
            )}

            <div className="aj-jar-actions">
              {!addingReason ? (
                <>
                  <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setAddingReason(true)}>
                    ➕ add a reason
                  </button>
                  {reasons.length > 0 && (
                    <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setJarOpen((o) => !o)}>
                      {jarOpen ? 'close the jar 🫙' : `peek into the jar (${reasons.length})`}
                    </button>
                  )}
                </>
              ) : (
                <form className="aj-jar-form" onSubmit={addReason}>
                  <input
                    className="aj-input"
                    value={reasonText}
                    maxLength={500}
                    autoFocus
                    onChange={(e) => setReasonText(e.target.value)}
                    placeholder="because…"
                  />
                  <div className="aj-ev-formbtns">
                    <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setAddingReason(false)}>
                      cancel
                    </button>
                    <button type="submit" className="aj-btn aj-btn-sm" disabled={busy || !reasonText.trim()}>
                      drop it in 🫙
                    </button>
                  </div>
                </form>
              )}
            </div>

            {jarOpen && (
              <div className="aj-jar-list">
                {reasons.map((r, i) => (
                  <div className="aj-jar-chit" key={r.id}>
                    <span className="aj-jar-chit-num">#{i + 1}</span>
                    <span className="aj-jar-chit-text">{r.text}</span>
                    <button type="button" className="aj-del" onClick={() => delReason(r.id)} aria-label="remove reason">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ───────── love coupons ───────── */}
          <div className="aj-coupons">
            <div className="aj-lb-heading">love coupons 🎟️</div>
            {coupons.length === 0 && <div className="aj-muted">no coupons printed yet — make the first promise. 💕</div>}

            <div className="aj-coupon-grid">
              {coupons.map((c) => (
                <div className={`aj-coupon${c.redeemedAt ? ' is-redeemed' : ''}`} key={c.id}>
                  <button type="button" className="aj-del aj-coupon-del" onClick={() => delCoupon(c.id)} aria-label="delete coupon">
                    ×
                  </button>
                  <div className="aj-coupon-emoji" aria-hidden="true">{c.emoji}</div>
                  <div className="aj-coupon-title">{c.title}</div>
                  <div className="aj-coupon-from">a promise from {c.from}</div>
                  {c.redeemedAt ? (
                    <div className="aj-coupon-stamp">
                      redeemed by {c.redeemedBy}
                      <span>{prettyWhen(c.redeemedAt)}</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="aj-btn aj-btn-sm aj-coupon-redeem"
                      disabled={busy || c.from === me}
                      title={c.from === me ? "it's your promise — the other one redeems it 😄" : 'redeem this promise'}
                      onClick={() => redeem(c)}
                    >
                      {c.from === me ? 'your promise 🤝' : 'redeem 🎟️'}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="aj-jar-actions">
              {!addingCoupon ? (
                <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setAddingCoupon(true)}>
                  ➕ print a new coupon
                </button>
              ) : (
                <form className="aj-jar-form" onSubmit={addCoupon}>
                  <input
                    className="aj-input"
                    value={couponTitle}
                    maxLength={120}
                    autoFocus
                    onChange={(e) => setCouponTitle(e.target.value)}
                    placeholder={`I, ${me}, hereby promise…`}
                  />
                  <div className="aj-moods">
                    {COUPON_EMOJIS.map((em) => (
                      <button
                        type="button" key={em}
                        className={`aj-mood${couponEmoji === em ? ' is-on' : ''}`}
                        onClick={() => setCouponEmoji(em)}
                        aria-label={`emoji ${em}`}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                  <div className="aj-ev-formbtns">
                    <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setAddingCoupon(false)}>
                      cancel
                    </button>
                    <button type="submit" className="aj-btn aj-btn-sm" disabled={busy || !couponTitle.trim()}>
                      print it 🎟️
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {err && <div className="aj-err">{err}</div>}
        </>
      )}

      {toast && <div className="aj-ss-toast">{toast}</div>}
    </div>
  );
}
